/**
 * PostHog client initializer.
 *
 * Idempotent: safe to call from React StrictMode double-mount, server, or
 * any other place where module evaluation might happen twice. The library
 * exports a singleton; we just guard the init call.
 *
 * Inert without `NEXT_PUBLIC_POSTHOG_KEY` — the deploy pipeline can ship
 * this code before the env var is provisioned and nothing fires until the
 * key lands.
 *
 * Default config:
 *  - autocapture ON (clicks/inputs/form-submits) — gives us "what did the
 *    user do" on flows we haven't manually instrumented yet
 *  - capture_pageview OFF — Next.js App Router fires no native page events,
 *    so we drive `$pageview` from `PostHogProvider` watching `usePathname`
 *  - session_recording is DISABLED at init (`disable_session_recording`) and
 *    started only after a user logs in (see PostHogProvider). We record 100%
 *    of authenticated app sessions and skip anonymous/marketing traffic
 *    (cost + noise). `maskAllInputs` blacks out input values once recording
 *    starts; passwords are masked unconditionally by PostHog regardless.
 *  - persistence: 'localStorage+cookie' so anonymous → identified user
 *    continuity survives a refresh.
 *  - surveys are skipped on the marketing surface (a <meta name="vr-surface">
 *    stamped by the (public) layout): posthog-js fetches surveys.js on every
 *    load otherwise, ~34 KB compressed, even when the project has no survey.
 */
import posthog, { type PostHog, type CaptureResult, type CapturedNetworkRequest } from "posthog-js";
import { shouldIgnoreError } from "@/lib/errorFilter";
import { POSTHOG_SESSION_HEADER, validPosthogSessionId } from "@/lib/posthogSessionId";

let initialized = false;

/**
 * Drop un-actionable `$exception` events before they reach Error Tracking.
 *
 * posthog-js' exception autocapture hooks window.onerror / onunhandledrejection
 * DIRECTLY, bypassing the app's own window handlers (and the allowlist they
 * apply). This re-applies the shared `shouldIgnoreError` predicate inside
 * `before_send` so browser/extension/third-party noise (ResizeObserver loop,
 * "Script error.", Stripe iframe races, transient network blips) does not page
 * anyone. Only `$exception` events are inspected; all other events pass through
 * untouched. (Inbox-429 / autocapture-bypass audit, 2026-06-27.)
 */
/**
 * Seats: the invitation link carries a one-time token in its fragment (#token=…). The invitation
 * page strips it before PostHog starts, but the address can carry it again (a sign-out that reloads
 * the page with it, a second link pasted into the same tab), and $pageleave reads the live address.
 * No event leaves with that fragment.
 */
export function stripTokenFragment(cr: CaptureResult | null): CaptureResult | null {
  const props = cr?.properties as Record<string, unknown> | undefined;
  const url = props?.$current_url;
  if (typeof url === "string" && /#.*token=/.test(url)) props!.$current_url = url.split("#")[0];
  return cr;
}

/**
 * Replay network capture, for the same token: a page's navigation timing entry keeps the address it
 * was first loaded with, fragment included, after the page strips it, and recordInitialRequests sends
 * that entry. Supplying this function replaces posthog-js's own body scrubbing, so bodies are dropped
 * here too (none are captured today: turning body capture on needs a scrubber first).
 */
export function maskReplayNetworkRequest(r: CapturedNetworkRequest): CapturedNetworkRequest {
  return { ...r, name: typeof r.name === "string" ? r.name.split("#")[0] : r.name, requestBody: null, responseBody: null };
}

export function filterIgnoredExceptions(cr: CaptureResult | null): CaptureResult | null {
  if (!cr || cr.event !== "$exception") return cr;
  const props = (cr.properties ?? {}) as Record<string, unknown>;
  const list = Array.isArray(props.$exception_list) ? props.$exception_list : [];
  const first = (list[0] ?? {}) as {
    type?: string;
    value?: string;
    stacktrace?: { frames?: Array<{ filename?: string }> };
  };
  const message =
    (typeof props.$exception_message === "string" && props.$exception_message) ||
    (typeof first.value === "string" && first.value) ||
    "";
  const frames = first.stacktrace?.frames;
  const source =
    Array.isArray(frames) && frames.length > 0
      ? frames[frames.length - 1]?.filename || frames[0]?.filename || ""
      : "";
  const type = typeof first.type === "string" ? first.type : undefined;
  // posthog-js types a DOMException by its class; its real name leads the value
  // ("AbortError: signal is aborted without reason"), so read the name from there.
  const errorName = type === "DOMException" ? message.split(":")[0] : type;
  return shouldIgnoreError(message, source, errorName) ? null : cr;
}

export function initPostHog(): PostHog | null {
  if (typeof window === "undefined") return null;
  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!key) return null;
  if (initialized) return posthog;

  const isDev = process.env.NODE_ENV !== "production";
  // Allow opting in to dev tracking when explicitly debugging the
  // integration — otherwise dev runs are kept out of the production
  // PostHog project to avoid polluting funnels.
  if (isDev && process.env.NEXT_PUBLIC_POSTHOG_DEV !== "1") return null;

  // True on the logged-out marketing / legal / docs surface (see the (public) layout). The pathname alone
  // cannot tell: "/" is the marketing page when logged out and the dashboard when logged in.
  const marketing = !!document.querySelector('meta[name="vr-surface"][content="marketing"]');

  posthog.init(key, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com",
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: true,
    capture_exceptions: true,
    // 2026-08-29: $rageclick fires on the funnel pages with a null element on
    // every one of them; dead clicks name the control. Cheap, and it is the
    // difference between "18 people bounced" and "18 people clicked X".
    capture_dead_clicks: true,
    // posthog-js downloads surveys.js (~34 KB br, ~91 KB parsed) on EVERY load, even when the project reports
    // surveys:false; only this flag stops it. Marketing visitors never see a survey; the app keeps the default so
    // launching one needs no code change. Not re-enabled on a client-side marketing -> app navigation: surveys
    // only start from the remote config seen at init, so that hop skips them until the next full page load.
    disable_surveys: marketing,
    // Re-apply the shared noise allowlist to autocaptured exceptions — see
    // filterIgnoredExceptions above (closes the posthog-js autocapture bypass).
    before_send: (cr) => filterIgnoredExceptions(stripTokenFragment(cr)),
    persistence: "localStorage+cookie",
    // Replay is gated to logged-in users: disabled at init, started after
    // login in PostHogProvider. Keeps anonymous/marketing sessions out of
    // replay while capturing 100% of authenticated app sessions.
    disable_session_recording: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "[data-mask]",
      maskCapturedNetworkRequestFn: maskReplayNetworkRequest,
    },
    loaded: (ph) => {
      // The deployed commit on every event (next.config.ts env), so an error
      // can be tied to the deploy that caused it: filter on app_version.
      ph.register({ app_version: process.env.APP_VERSION });
      // Only log in dev to keep production console clean
      if (isDev) {
        // eslint-disable-next-line no-console
        console.info("[posthog] initialized");
      }
    },
  });

  initialized = true;
  return posthog;
}

export function getPostHog(): PostHog | null {
  if (typeof window === "undefined") return null;
  if (!initialized) return null;
  return posthog;
}

/** This tab's PostHog session id, or null (not initialised, no session yet, or blocked). */
export function posthogSessionId(): string | null {
  try {
    return validPosthogSessionId(getPostHog()?.get_session_id());
  } catch {
    return null;
  }
}

/** `{ "X-PostHog-Session-Id": id }` for a fetch, or `{}` — never throws. */
export function posthogSessionHeaders(): Record<string, string> {
  const id = posthogSessionId();
  return id ? { [POSTHOG_SESSION_HEADER]: id } : {};
}

export function identifyUser(
  userId: string,
  traits?: {
    email?: string;
    name?: string;
    plan?: string;
    isTrial?: boolean;
    hasActiveSubscription?: boolean;
    isInternal?: boolean;
    /** ISO account-creation time — lets any insight split by signup cohort. */
    signupAt?: string | null;
    phoneVerified?: boolean;
    /** Seats: the account the identified person acts inside (equals the id for an owner). */
    accountId?: string;
    /** Seats: "member" filters replays and insights to team members. */
    role?: "owner" | "member";
  }
): void {
  const ph = getPostHog();
  if (!ph) return;
  ph.identify(userId, traits);
}

/**
 * Person properties that only the billing endpoints know (tier, pricing cohort,
 * referral). Called from BillingContext once /features resolves so every
 * insight, replay filter and survey can split cold vs affiliate without a DB
 * join. Inert when PostHog isn't initialized.
 */
export function setPersonProperties(
  props: Record<string, string | number | boolean | null | string[]>
): void {
  const ph = getPostHog();
  if (!ph) return;
  ph.setPersonProperties(props);
}

/**
 * Merge person properties onto the already-identified user without re-issuing
 * an identify. Used by PostHogIdentifyEnricher, which runs deeper in the tree
 * than the base identify (inside BillingProvider) and supplies plan/tier once
 * BillingContext resolves. No-op before init. Swallows its own errors —
 * telemetry must never affect product UX.
 */
export function setUserProperties(props: Record<string, unknown>): void {
  try {
    const ph = getPostHog();
    if (!ph) return;
    ph.setPersonProperties(props);
  } catch {
    /* swallow */
  }
}

export function resetUser(): void {
  const ph = getPostHog();
  if (!ph) return;
  ph.reset();
  // reset() clears super-properties; keep the deploy tag on post-sign-out events.
  ph.register({ app_version: process.env.APP_VERSION });
}

/**
 * Fire-and-forget event tracking. No-ops on server, before init, or when the
 * PostHog key is unset (dev without NEXT_PUBLIC_POSTHOG_DEV=1, e.g.). Errors
 * are swallowed — telemetry must never affect product UX.
 */
export function trackEvent(
  name: string,
  properties?: Record<string, unknown>,
): void {
  try {
    const ph = getPostHog();
    if (!ph) return;
    ph.capture(name, properties);
  } catch {
    /* swallow */
  }
}

/**
 * Report a caught exception to PostHog Error Tracking. Used by error
 * boundaries + window-level handlers — posthog-js' `capture_exceptions`
 * only sees uncaught errors, so boundary-caught React errors never reach
 * PostHog unless we forward them here.
 *
 * No-op when PostHog isn't initialized. Swallows its own errors — a
 * logging path that throws is worse than no logging.
 */
export function captureException(
  error: unknown,
  context?: Record<string, unknown>,
): void {
  try {
    const ph = getPostHog();
    if (!ph) return;
    const err = error instanceof Error ? error : new Error(String(error));
    ph.captureException(err, context);
  } catch {
    /* swallow */
  }
}
